import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ClipboardCheck,
  Plus,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Camera,
  Loader2,
  Shield,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useWorkOrderInspections,
  useCreateInspection,
  useCompleteInspection,
  useAddDefect,
  useDefectTypes,
} from '@/hooks/useQualityInspections';
import {
  INSPECTION_TYPE_LABELS,
  INSPECTION_STATUS_COLORS,
  INSPECTION_RESULT_LABELS,
  INSPECTION_RESULT_COLORS,
  DEFECT_SEVERITY_LABELS,
  DEFECT_SEVERITY_COLORS,
  INSPECTION_POINTS,
  type InspectionType,
  type InspectionResult,
  type DefectSeverity,
  type QualityInspection,
} from '@/types/quality';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface QualityInspectionPanelProps {
  workOrderId: string;
  processRecordId?: string;
  plybookExecutionId?: string;
  currentPlyNumber?: number;
  inspectionPoint?: string;
  compact?: boolean;
}

export function QualityInspectionPanel({
  workOrderId,
  processRecordId,
  plybookExecutionId,
  currentPlyNumber,
  inspectionPoint,
  compact = false,
}: QualityInspectionPanelProps) {
  const { user, userRole } = useAuth();
  const [expanded, setExpanded] = useState(!compact);
  const [newInspectionOpen, setNewInspectionOpen] = useState(false);
  const [activeInspection, setActiveInspection] = useState<QualityInspection | null>(null);
  const [addDefectOpen, setAddDefectOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  // Form states
  const [inspectionType, setInspectionType] = useState<InspectionType>('visual');
  const [selectedPoint, setSelectedPoint] = useState(inspectionPoint || '');
  const [notes, setNotes] = useState('');
  const [defectType, setDefectType] = useState('');
  const [defectSeverity, setDefectSeverity] = useState<DefectSeverity>('minor');
  const [defectLocation, setDefectLocation] = useState('');
  const [defectDescription, setDefectDescription] = useState('');
  const [result, setResult] = useState<InspectionResult>('approved');
  const [requiresRework, setRequiresRework] = useState(false);
  const [reworkDescription, setReworkDescription] = useState('');

  // Data
  const { data: inspections, isLoading } = useWorkOrderInspections(workOrderId);
  const { data: defectTypes } = useDefectTypes();
  const createMutation = useCreateInspection();
  const completeMutation = useCompleteInspection();
  const addDefectMutation = useAddDefect();

  const canInspect = userRole === 'quality' || userRole === 'admin' || userRole === 'supervisor';
  const pendingInspections = inspections?.filter(i => i.status !== 'completed') || [];
  const completedInspections = inspections?.filter(i => i.status === 'completed') || [];

  const handleStartInspection = async () => {
    if (!user) return;

    await createMutation.mutateAsync({
      work_order_id: workOrderId,
      process_record_id: processRecordId,
      plybook_execution_id: plybookExecutionId,
      inspection_type: inspectionType,
      inspection_point: selectedPoint || inspectionPoint,
      ply_number: currentPlyNumber,
      inspector_id: user.id,
      notes,
    });

    setNewInspectionOpen(false);
    setNotes('');
  };

  const handleAddDefect = async () => {
    if (!activeInspection) return;

    await addDefectMutation.mutateAsync({
      inspection_id: activeInspection.id,
      work_order_id: workOrderId,
      defect_type: defectType,
      severity: defectSeverity,
      location: defectLocation || undefined,
      description: defectDescription || undefined,
    });

    setAddDefectOpen(false);
    setDefectType('');
    setDefectSeverity('minor');
    setDefectLocation('');
    setDefectDescription('');
  };

  const handleCompleteInspection = async () => {
    if (!activeInspection) return;

    await completeMutation.mutateAsync({
      id: activeInspection.id,
      work_order_id: workOrderId,
      result,
      notes,
      requires_rework: requiresRework,
      rework_description: requiresRework ? reworkDescription : undefined,
    });

    setCompleteDialogOpen(false);
    setActiveInspection(null);
    setResult('approved');
    setRequiresRework(false);
    setReworkDescription('');
    setNotes('');
  };

  if (!canInspect && pendingInspections.length === 0 && completedInspections.length === 0) {
    return null;
  }

  // Compact view for embedding in lamination view
  if (compact && !expanded) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setExpanded(true)}
        className="gap-2 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
      >
        <Shield className="w-4 h-4" />
        Calidad
        {pendingInspections.length > 0 && (
          <Badge variant="destructive" className="ml-1">
            {pendingInspections.length}
          </Badge>
        )}
      </Button>
    );
  }

  return (
    <Card className={cn(compact && "border-amber-500/30")}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardCheck className="w-5 h-5 text-amber-500" />
            Control de Calidad
            {pendingInspections.length > 0 && (
              <Badge variant="outline" className="border-amber-500 text-amber-500">
                {pendingInspections.length} activas
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {canInspect && (
              <Button
                size="sm"
                onClick={() => setNewInspectionOpen(true)}
                className="gap-1 bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4" />
                Inspección
              </Button>
            )}
            {compact && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setExpanded(false)}
                className="h-8 w-8"
              >
                <ChevronUp className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Active inspections */}
            {pendingInspections.map((inspection) => (
              <div
                key={inspection.id}
                className="p-3 rounded-lg border-2 border-amber-500/50 bg-amber-500/5"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={INSPECTION_STATUS_COLORS[inspection.status]}>
                        {inspection.status === 'in_progress' ? 'En curso' : 'Pendiente'}
                      </Badge>
                      <span className="text-sm font-medium">
                        {INSPECTION_TYPE_LABELS[inspection.inspection_type]}
                      </span>
                    </div>
                    {inspection.inspection_point && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Punto: {INSPECTION_POINTS.find(p => p.value === inspection.inspection_point)?.label || inspection.inspection_point}
                        {inspection.ply_number && ` - Capa #${inspection.ply_number}`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {inspection.created_at && format(new Date(inspection.created_at), 'HH:mm', { locale: es })}
                  </div>
                </div>

                {/* Defects count */}
                {inspection.has_defects && (
                  <div className="flex items-center gap-2 mb-2 text-sm text-red-500">
                    <AlertTriangle className="w-4 h-4" />
                    {inspection.defect_count} defecto(s) encontrado(s)
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setActiveInspection(inspection);
                      setAddDefectOpen(true);
                    }}
                    className="gap-1"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Defecto
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setActiveInspection(inspection);
                      setCompleteDialogOpen(true);
                    }}
                    className="gap-1 flex-1"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Completar
                  </Button>
                </div>
              </div>
            ))}

            {/* Completed inspections summary */}
            {completedInspections.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">
                  Inspecciones completadas: {completedInspections.length}
                </p>
                <div className="flex flex-wrap gap-1">
                  {completedInspections.slice(0, 5).map((inspection) => (
                    <Badge
                      key={inspection.id}
                      className={cn(
                        "text-xs",
                        inspection.result && INSPECTION_RESULT_COLORS[inspection.result]
                      )}
                    >
                      {inspection.result === 'approved' ? (
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                      ) : (
                        <XCircle className="w-3 h-3 mr-1" />
                      )}
                      {INSPECTION_RESULT_LABELS[inspection.result!]}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {pendingInspections.length === 0 && completedInspections.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No hay inspecciones registradas
              </p>
            )}
          </>
        )}
      </CardContent>

      {/* New Inspection Dialog */}
      <Dialog open={newInspectionOpen} onOpenChange={setNewInspectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Inspección de Calidad</DialogTitle>
            <DialogDescription>
              Registra una inspección en cualquier punto del proceso
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Tipo de inspección</label>
                <Select value={inspectionType} onValueChange={(v) => setInspectionType(v as InspectionType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(INSPECTION_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Punto de inspección</label>
                <Select value={selectedPoint} onValueChange={setSelectedPoint}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INSPECTION_POINTS.map((point) => (
                      <SelectItem key={point.value} value={point.value}>
                        {point.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {currentPlyNumber && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm">
                  <strong>Capa actual:</strong> #{currentPlyNumber}
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Notas iniciales</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones al iniciar la inspección..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNewInspectionOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleStartInspection}
              disabled={createMutation.isPending}
              className="gap-2"
            >
              {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Iniciar Inspección
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Defect Dialog */}
      <Dialog open={addDefectOpen} onOpenChange={setAddDefectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Registrar Defecto
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Tipo de defecto *</label>
              <Select value={defectType} onValueChange={setDefectType}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {defectTypes?.filter(dt => dt.name).map((dt) => (
                    <SelectItem key={dt.id} value={dt.name}>
                      <div className="flex items-center gap-2">
                        <Badge className={DEFECT_SEVERITY_COLORS[dt.severity_default]} variant="outline">
                          {DEFECT_SEVERITY_LABELS[dt.severity_default]}
                        </Badge>
                        {dt.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Severidad</label>
              <Select value={defectSeverity} onValueChange={(v) => setDefectSeverity(v as DefectSeverity)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DEFECT_SEVERITY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        <div className={cn("w-3 h-3 rounded-full", {
                          "bg-red-600": value === 'critical',
                          "bg-orange-500": value === 'major',
                          "bg-yellow-500": value === 'minor',
                          "bg-blue-400": value === 'cosmetic',
                        })} />
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Ubicación</label>
              <Textarea
                value={defectLocation}
                onChange={(e) => setDefectLocation(e.target.value)}
                placeholder="Ej: Zona 2, borde superior..."
                rows={1}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Descripción</label>
              <Textarea
                value={defectDescription}
                onChange={(e) => setDefectDescription(e.target.value)}
                placeholder="Descripción detallada del defecto..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDefectOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddDefect}
              disabled={!defectType || addDefectMutation.isPending}
              variant="destructive"
              className="gap-2"
            >
              {addDefectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Registrar Defecto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Inspection Dialog */}
      <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Completar Inspección</AlertDialogTitle>
            <AlertDialogDescription>
              {activeInspection?.has_defects
                ? `Se encontraron ${activeInspection.defect_count} defecto(s). Selecciona el resultado de la inspección.`
                : 'No se registraron defectos. Confirma el resultado de la inspección.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Resultado</label>
              <Select value={result} onValueChange={(v) => setResult(v as InspectionResult)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(INSPECTION_RESULT_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        {value === 'approved' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                        {value === 'rejected' && <XCircle className="w-4 h-4 text-red-500" />}
                        {value === 'conditional' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                        {value === 'on_hold' && <Clock className="w-4 h-4 text-gray-500" />}
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(result === 'rejected' || result === 'conditional') && (
              <div className="space-y-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="requiresRework"
                    checked={requiresRework}
                    onChange={(e) => setRequiresRework(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="requiresRework" className="text-sm font-medium">
                    Requiere retrabajo
                  </label>
                </div>

                {requiresRework && (
                  <Textarea
                    value={reworkDescription}
                    onChange={(e) => setReworkDescription(e.target.value)}
                    placeholder="Describe el retrabajo necesario..."
                    rows={2}
                  />
                )}
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Notas finales</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones adicionales..."
                rows={2}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCompleteInspection}
              disabled={completeMutation.isPending}
              className={cn(
                result === 'approved' && "bg-green-600 hover:bg-green-700",
                result === 'rejected' && "bg-red-600 hover:bg-red-700",
                result === 'conditional' && "bg-amber-600 hover:bg-amber-700"
              )}
            >
              {completeMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirmar {INSPECTION_RESULT_LABELS[result]}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
